import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Button } from '../components/ui/button';

export const ProductCatalogue = () => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);

  const pdfUrl = 'https://customer-assets.emergentagent.com/job_fanco-preview/artifacts/st5r04ai_Product%20Catalogue%202026.pdf';

  useEffect(() => {
    document.title = 'Product Catalogue 2026';
    window.scrollTo(0, 0);

    // Load PDF.js from CDN
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      loadPDF();
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const loadPDF = async () => {
    try {
      const pdf = await window.pdfjsLib.getDocument(pdfUrl).promise;
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setLoading(false);
    } catch (error) {
      console.error('Error loading PDF:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pdfDoc && canvasRef.current) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage]);

  const renderPage = async (pageNum) => {
    const page = await pdfDoc.getPage(pageNum);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    const containerWidth = canvas.parentElement.offsetWidth;
    const viewport = page.getViewport({ scale: 1 });
    const scale = containerWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale: scale * 1.5 });

    canvas.height = scaledViewport.height;
    canvas.width = scaledViewport.width;

    await page.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < numPages) setCurrentPage(currentPage + 1);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <div className="flex-1 pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl font-light tracking-tight text-stone-900 mb-4 text-center">
            Product Catalogue 2026
          </h1>
          
          <div className="flex items-center justify-center gap-4 mb-6">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              download="Product Catalogue 2026.pdf"
            >
              <Button variant="outline" className="text-stone-700 border-stone-300">
                <Download className="mr-2" size={16} />
                Download PDF
              </Button>
            </a>
          </div>

          {loading ? (
            <div className="flex items-center justify-center" style={{ minHeight: '600px' }}>
              <p className="text-stone-500">Loading catalogue...</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-4 mb-4">
                <Button 
                  variant="outline" 
                  onClick={goToPrevPage} 
                  disabled={currentPage <= 1}
                  className="text-stone-700 border-stone-300"
                >
                  <ChevronLeft size={20} />
                </Button>
                <span className="text-stone-600">
                  Page {currentPage} of {numPages}
                </span>
                <Button 
                  variant="outline" 
                  onClick={goToNextPage} 
                  disabled={currentPage >= numPages}
                  className="text-stone-700 border-stone-300"
                >
                  <ChevronRight size={20} />
                </Button>
              </div>

              <div className="w-full flex justify-center bg-stone-100 rounded-lg p-4 overflow-auto">
                <canvas ref={canvasRef} className="shadow-lg" />
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};
